const SEARCH_INDEX_URL = "/assets/data/search-index.json";
const SEARCH_DEBOUNCE_MS = 120;

const normalizeSearchQuery = (value = "") => value.trim().toLowerCase();

const getHaystack = (entry) =>
  `${entry?.title || ""} ${entry?.excerpt || ""} ${entry?.category || ""}`.toLowerCase();

const uniqueCategories = (items) => {
  const seen = new Set();
  const out = [];
  items.forEach((item) => {
    const category = (item?.category || "").trim();
    if (!category || seen.has(category)) return;
    seen.add(category);
    out.push(category);
  });
  return out;
};

const clearChildren = (el) => {
  if (!el) return;
  el.innerHTML = "";
};

const setSearchUiState = (
  { searchResultsSection, searchEmpty, searchTags },
  { showResultsSection, showEmpty, showTags }
) => {
  searchResultsSection?.toggleAttribute("hidden", !showResultsSection);
  searchEmpty?.toggleAttribute("hidden", !showEmpty);
  searchTags?.toggleAttribute("hidden", !showTags);
};

const createResultItem = (documentCtx, item) => {
  const li = documentCtx.createElement("li");
  li.className = "header__search-list-item";

  const article = documentCtx.createElement("article");
  article.className = "post-card post-card--xl post-card--headline-listing";

  const header = documentCtx.createElement("header");
  header.className = "post-card__header";

  if (item.category) {
    const categoryWrap = documentCtx.createElement("div");
    categoryWrap.className = "post-card__category";

    const categoryText = documentCtx.createElement("span");
    categoryText.className = "post-card__category-text";
    categoryText.textContent = item.category;

    categoryWrap.appendChild(categoryText);
    header.appendChild(categoryWrap);
  }

  if (item.title) {
    const titleEl = documentCtx.createElement("h3");
    titleEl.className = "post-card__title";

    const link = documentCtx.createElement("a");
    link.className = "post-card__title-link";
    link.href = item.href || "#";
    link.textContent = item.title;

    titleEl.appendChild(link);
    header.appendChild(titleEl);
  }

  const body = documentCtx.createElement("div");
  body.className = "post-card__body";

  const content = documentCtx.createElement("div");
  content.className = "post-card__content";

  if (item.excerpt) {
    const excerptWrap = documentCtx.createElement("div");
    excerptWrap.className = "post-card__excerpt";
    const p = documentCtx.createElement("p");
    p.textContent = item.excerpt;
    excerptWrap.appendChild(p);
    content.appendChild(excerptWrap);
  }

  body.appendChild(content);

  const footer = documentCtx.createElement("footer");
  footer.className = "post-card__footer";
  if (item.date?.text) {
    const timeEl = documentCtx.createElement("time");
    timeEl.className = "post-card__date";
    timeEl.setAttribute("datetime", item.date?.datetime || "");
    timeEl.textContent = item.date.text;
    footer.appendChild(timeEl);
  }

  article.appendChild(header);
  article.appendChild(body);
  if (footer.childNodes.length) {
    article.appendChild(footer);
  }

  li.appendChild(article);
  return li;
};

const renderCategories = (documentCtx, listEl, categories) => {
  clearChildren(listEl);
  categories.forEach((category) => {
    const li = documentCtx.createElement("li");
    const a = documentCtx.createElement("a");
    a.href = "#";
    a.textContent = category;
    li.appendChild(a);
    listEl?.appendChild(li);
  });
};

export const createHeaderSearchController = (documentReference, headerRoot) => {
  const searchInput = headerRoot.querySelector(".global-search__input");
  const searchForm = headerRoot.querySelector("[data-header-search-form]");
  const searchField = headerRoot.querySelector(".global-search__field");
  const searchTags = headerRoot.querySelector("[data-header-search-tags]");
  const searchResultsSection = headerRoot.querySelector("[data-header-search-results]");
  const searchResultsList = headerRoot.querySelector("[data-header-search-list]");
  const searchEmpty = headerRoot.querySelector("[data-header-search-empty]");

  if (
    !searchInput ||
    !searchField ||
    !searchResultsSection ||
    !searchTags ||
    !searchResultsList ||
    !searchEmpty
  ) {
    return { render: () => {}, destroy: () => {} };
  }

  const view = documentReference.defaultView;
  let searchIndexPromise = null;
  let searchDebounceTimer = null;

  const getSearchIndex = async () => {
    if (searchIndexPromise) return searchIndexPromise;

    searchIndexPromise = (async () => {
      try {
        const response = await fetch(SEARCH_INDEX_URL, { credentials: "same-origin" });
        if (!response.ok) return [];
        const data = await response.json();
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    })();

    return searchIndexPromise;
  };

  const applyEmptyState = () => {
    clearChildren(searchResultsList);
    searchField.classList.remove("global-search__field--not-found");
    setSearchUiState(
      { searchResultsSection, searchEmpty, searchTags },
      { showResultsSection: false, showEmpty: false, showTags: false }
    );
  };

  const render = async () => {
    const query = normalizeSearchQuery(searchInput.value || "");
    if (!query) {
      applyEmptyState();
      return;
    }

    const index = await getSearchIndex();
    const results = index.filter((entry) => getHaystack(entry).includes(query));
    clearChildren(searchResultsList);

    if (!results.length) {
      clearChildren(searchTags);
      searchField.classList.add("global-search__field--not-found");
      setSearchUiState(
        { searchResultsSection, searchEmpty, searchTags },
        { showResultsSection: false, showEmpty: true, showTags: false }
      );
      return;
    }

    searchField.classList.remove("global-search__field--not-found");
    results.forEach((item) => {
      searchResultsList.appendChild(createResultItem(documentReference, item));
    });

    const categories = uniqueCategories(results);
    renderCategories(documentReference, searchTags, categories);

    setSearchUiState(
      { searchResultsSection, searchEmpty, searchTags },
      { showResultsSection: true, showEmpty: false, showTags: categories.length > 0 }
    );
  };

  const scheduleRender = () => {
    if (searchDebounceTimer) {
      view?.clearTimeout(searchDebounceTimer);
    }
    searchDebounceTimer = view?.setTimeout(() => {
      render();
    }, SEARCH_DEBOUNCE_MS);
  };

  const onInput = () => scheduleRender();
  const onFocus = () => scheduleRender();
  const onSubmit = (event) => {
    event.preventDefault();
    render();
  };

  searchInput.addEventListener("input", onInput);
  searchInput.addEventListener("focus", onFocus);
  searchForm?.addEventListener("submit", onSubmit);

  // стартовое состояние — скрыто (пока нет ввода)
  applyEmptyState();

  return {
    render,
    destroy: () => {
      if (searchDebounceTimer) {
        view?.clearTimeout(searchDebounceTimer);
      }
      searchInput.removeEventListener("input", onInput);
      searchInput.removeEventListener("focus", onFocus);
      searchForm?.removeEventListener("submit", onSubmit);
    }
  };
};
